import {z} from 'zod';

export const CURVE_DIRECTIONS = ['in', 'out', 'inOut'] as const;
export const ROUGH_TEMPLATE_CURVE_VALUES = [
  'none.in',
  'none.out',
  'none.inOut',
  'power1.in',
  'power1.out',
  'power1.inOut',
  'power2.in',
  'power2.out',
  'power2.inOut',
  'power3.in',
  'power3.out',
  'power3.inOut',
  'power4.in',
  'power4.out',
  'power4.inOut',
  'expo.in',
  'expo.out',
  'expo.inOut',
  'sine.in',
  'sine.out',
  'sine.inOut',
  'back.in',
  'back.out',
  'back.inOut',
  'bounce.in',
  'bounce.out',
  'bounce.inOut',
  'circ.in',
  'circ.out',
  'circ.inOut',
  'elastic.in',
  'elastic.out',
  'elastic.inOut',
] as const;
export const EXPO_SCALE_TEMPLATE_CURVE_VALUES = [
  'none.in',
  'none.out',
  'none.inOut',
  'power1.in',
  'power1.out',
  'power1.inOut',
  'power2.in',
  'power2.out',
  'power2.inOut',
  'power3.in',
  'power3.out',
  'power3.inOut',
  'power4.in',
  'power4.out',
  'power4.inOut',
  'expo.in',
  'expo.out',
  'expo.inOut',
  'sine.in',
  'sine.out',
  'sine.inOut',
] as const;
export const ROUGH_TAPER_VALUES = ['none', 'in', 'out', 'both'] as const;
export const CUSTOM_WIGGLE_TYPES = [
  'easeOut',
  'easeInOut',
  'anticipate',
  'uniform',
  'random',
] as const;

export const curveDirectionSchema = z.enum(CURVE_DIRECTIONS);
export const roughTemplateCurveSchema = z.enum(ROUGH_TEMPLATE_CURVE_VALUES);
export const expoScaleTemplateCurveSchema = z.enum(
  EXPO_SCALE_TEMPLATE_CURVE_VALUES
);
const roughTaperSchema = z.enum(ROUGH_TAPER_VALUES);
const customWiggleTypeSchema = z.enum(CUSTOM_WIGGLE_TYPES);

export const backEaseSchema = z
  .object({
    type: z.literal('back'),
    curve: curveDirectionSchema,
    power: z.number(),
  })
  .strict();

export const elasticEaseSchema = z
  .object({
    type: z.literal('elastic'),
    curve: curveDirectionSchema,
    amplitude: z.number(),
    period: z.number(),
  })
  .strict();

export const stepsEaseSchema = z
  .object({
    type: z.literal('steps'),
    stepCount: z.number().int(),
  })
  .strict();

export const roughEaseSchema = z
  .object({
    type: z.literal('rough'),
    templateCurve: roughTemplateCurveSchema,
    points: z.number().int(),
    strength: z.number(),
    taper: roughTaperSchema,
    randomizePoints: z.boolean(),
    clampPoints: z.boolean(),
  })
  .strict();

export const slowMoEaseSchema = z
  .object({
    type: z.literal('slowMo'),
    linearRatio: z.number(),
    power: z.number(),
    yoyoMode: z.boolean(),
  })
  .strict();

export const expoScaleEaseSchema = z
  .object({
    type: z.literal('expoScale'),
    startingScale: z.number(),
    endingScale: z.number(),
    templateCurve: expoScaleTemplateCurveSchema,
  })
  .strict();

export const customWiggleEaseSchema = z
  .object({
    type: z.literal('customWiggle'),
    wiggles: z.number().int(),
    wiggleType: customWiggleTypeSchema,
  })
  .strict();

export const customBounceEaseSchema = z
  .object({
    type: z.literal('customBounce'),
    strength: z.number(),
    squash: z.number(),
    endAtStart: z.boolean(),
  })
  .strict();

export const customEaseSchema = z
  .object({
    type: z.literal('customEase'),
    bezierCurve: z.string(),
  })
  .strict();

export const advancedEaseSchema = z.discriminatedUnion('type', [
  backEaseSchema,
  elasticEaseSchema,
  stepsEaseSchema,
  roughEaseSchema,
  slowMoEaseSchema,
  expoScaleEaseSchema,
  customWiggleEaseSchema,
  customBounceEaseSchema,
  customEaseSchema,
]);

export const easeConfigSchema = z.union([
  z.number().int().nonnegative(),
  advancedEaseSchema,
]);

export type CurveDirection = z.infer<typeof curveDirectionSchema>;
export type RoughTemplateCurve = z.infer<typeof roughTemplateCurveSchema>;
export type ExpoScaleTemplateCurve = z.infer<
  typeof expoScaleTemplateCurveSchema
>;
export type RoughTaper = z.infer<typeof roughTaperSchema>;
export type CustomWiggleType = z.infer<typeof customWiggleTypeSchema>;
export type BackEase = z.infer<typeof backEaseSchema>;
export type ElasticEase = z.infer<typeof elasticEaseSchema>;
export type StepsEase = z.infer<typeof stepsEaseSchema>;
export type RoughEase = z.infer<typeof roughEaseSchema>;
export type SlowMoEase = z.infer<typeof slowMoEaseSchema>;
export type ExpoScaleEase = z.infer<typeof expoScaleEaseSchema>;
export type CustomWiggleEase = z.infer<typeof customWiggleEaseSchema>;
export type CustomBounceEase = z.infer<typeof customBounceEaseSchema>;
export type CustomEase = z.infer<typeof customEaseSchema>;
export type AdvancedEase = z.infer<typeof advancedEaseSchema>;
export type EaseConfig = z.infer<typeof easeConfigSchema>;

/**
 * Numeric index into EASING_NAMES array
 * Represents a built-in easing function for animations
 */
export const easingTypeSchema = z.number().int().nonnegative();
export type EasingType = z.infer<typeof easingTypeSchema>;

/**
 * Legacy support for custom bezier curves (4-tuple format)
 * @example [0.25, 0.1, 0.25, 1] // ease
 * @deprecated Use CustomEase from this module instead
 */
export const customBezierSchema = z
  .tuple([
    z.number().min(0).max(1),
    z.number(),
    z.number().min(0).max(1),
    z.number(),
  ])
  .describe('Custom bezier curve [x1, y1, x2, y2]');
export type CustomBezier = z.infer<typeof customBezierSchema>;

export function isBuiltInEasing(ease: EaseConfig | null): ease is EasingType {
  return typeof ease === 'number';
}

export function isAdvancedEase(ease: EaseConfig | null): ease is AdvancedEase {
  return typeof ease === 'object' && ease !== null && 'type' in ease;
}

export function isCustomEaseType(
  ease: EaseConfig
): ease is CustomEase | CustomBounceEase | CustomWiggleEase | ExpoScaleEase {
  if (!isAdvancedEase(ease)) return false;

  return (
    ease.type === 'customEase' ||
    ease.type === 'customBounce' ||
    ease.type === 'customWiggle' ||
    ease.type === 'expoScale'
  );
}

/**
 * Primary easing schema - use this in other schemas
 */
export const easingSchema = easeConfigSchema;

/**
 * Legacy type alias for backward compatibility
 * @deprecated Use EaseConfig instead
 */
export type EasingRef = EaseConfig;
